import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    User,
    Activity,
    Key,
    LogOut,
    Copy,
    Check,
    ArrowLeft,
    Loader2,
    MessageSquare,
    BarChart3,
    TrendingUp,
    Save,
    History,
    DollarSign,
    Monitor,
    Smartphone,
    Trash2,
} from 'lucide-react';
import {
    getUserMachines,
    deactivateMachine,
    getRemainingDeactivations
} from '../../services/licenseService';
import './Dashboard.css';

export default function Dashboard() {
    const { user, signOut, updateProfile } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);

    // Profile editing state
    const [editingName, setEditingName] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

    // Real data from Supabase
    const [profile, setProfile] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [stats, setStats] = useState({
        tokenBalance: 0,
        tokensUsed: 0,
        chatsCount: 0,
        snapshotsCount: 0,
        totalCost: 0,
        apiLimitUsd: 0,
        apiUsedUsd: 0,
    });
    const [recentChats, setRecentChats] = useState([]);
    const [recentSnapshots, setRecentSnapshots] = useState([]);
    const [apiLogs, setApiLogs] = useState([]);
    const [dailyUsage, setDailyUsage] = useState([]);
    const hasFetched = React.useRef(false);

    // New state for expanded features
    const [billingHistory, setBillingHistory] = useState([]);
    const [modelBreakdown, setModelBreakdown] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [timePeriod, setTimePeriod] = useState('week'); // 'day', 'week', 'month'
    const [budgetLimit, setBudgetLimit] = useState(null);
    const [savingBudget, setSavingBudget] = useState(false);

    // Device management state
    const [machines, setMachines] = useState([]);
    const [remainingDeactivations, setRemainingDeactivations] = useState(0);
    const [deactivatingId, setDeactivatingId] = useState(null);
    const [deviceMessage, setDeviceMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        // Only fetch once on initial mount, not on every re-render/tab switch
        if (user && !hasFetched.current) {
            hasFetched.current = true;
            fetchDashboardData();
        }
    }, [user]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // Fetch profile with token data
            const { data: profileData } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();

            if (profileData) {
                setProfile(profileData);
                setStats(prev => ({
                    ...prev,
                    tokenBalance: profileData.token_balance || 0,
                    tokensUsed: profileData.tokens_used || 0,
                }));
            }

            // Fetch active subscription & app settings from Supabase
            const { data: subscriptionData } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .eq('status', 'active')
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            const { data: appSettingsData } = await supabase
                .from('app_settings')
                .select('*');

            if (subscriptionData) {
                setSubscription(subscriptionData);
                const matchedSetting = (appSettingsData || []).find(
                    s => s.plan_name?.toLowerCase() === subscriptionData.plan_type?.toLowerCase()
                );

                const planCost = matchedSetting?.subscription_cost != null && Number(matchedSetting.subscription_cost) > 0
                    ? Number(matchedSetting.subscription_cost)
                    : (parseFloat(subscriptionData.captured_cost) || (subscriptionData.plan_type === 'studio' ? 50 : subscriptionData.plan_type === 'enterprise' ? 200 : subscriptionData.plan_type === 'pro' ? 19 : 0));

                const internalLimit = matchedSetting?.api_limit_usd != null && Number(matchedSetting.api_limit_usd) > 0
                    ? Number(matchedSetting.api_limit_usd)
                    : (planCost > 0 ? Number((planCost * 0.65).toFixed(2)) : 0);

                setStats(prev => ({
                    ...prev,
                    userPlanPrice: planCost,
                    internalApiLimitUsd: internalLimit,
                    apiLimitUsd: planCost > 0 ? planCost : (parseFloat(subscriptionData.api_calls_limit_usd) || 0),
                    apiUsedUsd: parseFloat(subscriptionData.api_calls_used_usd) || 0,
                }));
            }

            // Fetch messages with is_byok flag
            const { data: messagesData } = await supabase
                .from('messages')
                .select('tokens, cost, created_at, is_byok')
                .eq('user_id', user.id);

            // Fetch images with is_byok flag  
            const { data: imagesData } = await supabase
                .from('images')
                .select('tokens, cost, created_at, is_byok')
                .eq('user_id', user.id);

            // Segregate Managed Proxy usage vs BYOK usage
            const managedProxyMessages = (messagesData || []).filter(m => !m.is_byok);
            const managedProxyImages = (imagesData || []).filter(i => !i.is_byok);

            const byokMessages = (messagesData || []).filter(m => m.is_byok);
            const byokImages = (imagesData || []).filter(i => i.is_byok);

            const managedMessageCost = managedProxyMessages.reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);
            const managedImageCost = managedProxyImages.reduce((sum, i) => sum + (parseFloat(i.cost) || 0), 0);
            const managedProxyCost = managedMessageCost + managedImageCost;

            const byokMessageCost = byokMessages.reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);
            const byokImageCost = byokImages.reduce((sum, i) => sum + (parseFloat(i.cost) || 0), 0);
            const byokCost = byokMessageCost + byokImageCost;

            const messageTokens = (messagesData || []).reduce((sum, m) => sum + (parseInt(m.tokens) || 0), 0);
            const imageTokens = (imagesData || []).reduce((sum, i) => sum + (parseInt(i.tokens) || 0), 0);
            const totalTokensFromUsage = messageTokens + imageTokens;

            setStats(prev => ({
                ...prev,
                totalCost: managedProxyCost,
                byokCost: byokCost,
                rawTotalCost: managedProxyCost + byokCost,
                tokensUsed: totalTokensFromUsage || prev.tokensUsed,
            }));

            // Fetch chats count
            const { count: chatsCount } = await supabase
                .from('chats')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id);

            // Fetch recent chats
            const { data: chatsData } = await supabase
                .from('chats')
                .select('id, name, last_active')
                .eq('user_id', user.id)
                .order('last_active', { ascending: false })
                .limit(5);

            // Fetch snapshots count
            const { count: snapshotsCount } = await supabase
                .from('historical_snapshots')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id);

            // Fetch recent snapshots
            const { data: snapshotsData } = await supabase
                .from('historical_snapshots')
                .select('*')
                .eq('user_id', user.id)
                .order('date', { ascending: false })
                .limit(5);

            setStats(prev => ({
                ...prev,
                chatsCount: chatsCount || 0,
                snapshotsCount: snapshotsCount || 0,
            }));

            setRecentChats(chatsData || []);
            setRecentSnapshots(snapshotsData || []);

            // Generate daily usage data from real messages/images (last 7 days)
            const last7Days = [];
            const allUsageItems = [
                ...(messagesData || []).map(m => ({ ...m, type: 'message' })),
                ...(imagesData || []).map(i => ({ ...i, type: 'image' })),
            ];

            for (let i = 6; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

                // Filter items for this day
                const itemsOnDay = allUsageItems.filter(item => {
                    if (!item.created_at) return false;
                    const itemDate = new Date(item.created_at).toISOString().split('T')[0];
                    return itemDate === dateStr;
                });

                const tokensOnDay = itemsOnDay.reduce((sum, item) => sum + (parseInt(item.tokens) || 0), 0);
                const costOnDay = itemsOnDay.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);

                last7Days.push({
                    name: dayName,
                    tokens: tokensOnDay,
                    cost: costOnDay,
                    requests: itemsOnDay.length,
                });
            }
            setDailyUsage(last7Days);

            // Fetch API logs (cost tracking)
            const { data: logsData } = await supabase
                .from('api_usage')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(20);

            setApiLogs(logsData || []);

            // Fetch billing history from subscriptions (past and current)
            const { data: billingData } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(10);

            setBillingHistory(billingData || []);

            // Calculate model breakdown from messages
            const modelCosts = {};
            (messagesData || []).forEach(msg => {
                const model = msg.model || 'unknown';
                if (!modelCosts[model]) {
                    modelCosts[model] = { model, tokens: 0, cost: 0, count: 0 };
                }
                modelCosts[model].tokens += parseInt(msg.tokens) || 0;
                modelCosts[model].cost += parseFloat(msg.cost) || 0;
                modelCosts[model].count += 1;
            });
            setModelBreakdown(Object.values(modelCosts));

            // Fetch team members if user has a team
            if (profileData?.team_id) {
                const { data: teamData } = await supabase
                    .from('profiles')
                    .select('id, full_name, email, tokens_used')
                    .eq('team_id', profileData.team_id);
                setTeamMembers(teamData || []);
            }

            // Get user's budget limit if set
            if (profileData?.budget_limit_usd) {
                setBudgetLimit(profileData.budget_limit_usd);
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMachines = async () => {
        try {
            const data = await getUserMachines(user.id);
            setMachines(data);
            const remaining = await getRemainingDeactivations(user.id);
            setRemainingDeactivations(remaining);
        } catch (error) {
            console.error('Error loading machines:', error);
        }
    };

    useEffect(() => {
        if (activeTab === 'devices') {
            loadMachines();
        }
    }, [activeTab]);

    const handleRemoveMachine = async (machineId) => {
        if (!window.confirm('Are you sure you want to deactivate this device? You can only do this twice per month.')) {
            return;
        }

        setDeactivatingId(machineId);
        setDeviceMessage({ type: '', text: '' });

        try {
            await deactivateMachine(user.id, machineId);
            setDeviceMessage({ type: 'success', text: 'Device deactivated successfully.' });
            loadMachines();
        } catch (error) {
            setDeviceMessage({ type: 'error', text: error.message || 'Failed to deactivate device.' });
        } finally {
            setDeactivatingId(null);
        }
    };

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    const copyUserId = () => {
        navigator.clipboard.writeText(user?.id || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const handleSaveProfile = async () => {
        setSavingProfile(true);
        setProfileMessage({ type: '', text: '' });
        try {
            await updateProfile({ full_name: editingName.trim() });
            setProfile((prev) => ({ ...prev, full_name: editingName.trim() }));
            setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (error) {
            setProfileMessage({ type: 'error', text: error.message || 'Failed to update profile' });
        } finally {
            setSavingProfile(false);
        }
    };

    // Save user's budget limit
    const handleSaveBudgetLimit = async (newLimit) => {
        setSavingBudget(true);
        try {
            await supabase
                .from('profiles')
                .update({ budget_limit_usd: newLimit })
                .eq('id', user.id);
            setBudgetLimit(newLimit);
        } catch (error) {
            console.error('Error saving budget limit:', error);
        } finally {
            setSavingBudget(false);
        }
    };

    // Export usage data as CSV
    const exportUsageCSV = () => {
        const headers = ['Date', 'Type', 'Model', 'Tokens', 'Cost ($)'];
        const rows = apiLogs.map(log => [
            formatDate(log.created_at),
            log.type || 'API Call',
            log.model || 'N/A',
            log.tokens || 0,
            (log.cost || 0).toFixed(4),
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `revtrone-usage-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Estimate future costs based on current usage pattern
    const estimateMonthlyCost = () => {
        if (dailyUsage.length === 0) return 0;
        const avgDailyCost = dailyUsage.reduce((sum, d) => sum + (d.cost || 0), 0) / dailyUsage.length;
        return avgDailyCost * 30;
    };

    // Calculate budget usage percentage based on internal 65% limit
    const getBudgetPercentage = () => {
        if (subscription?.plan_type === 'free' || !subscription) return 0;
        const internalLimit = stats.internalApiLimitUsd || (stats.userPlanPrice ? stats.userPlanPrice * 0.65 : 12.35);
        if (internalLimit <= 0) return 0;
        return Math.min((stats.totalCost / internalLimit) * 100, 100);
    };

    // Customer-facing displayed cost (scaled so $12.35 raw cost displays as $19.00 budget spent)
    const getDisplayedCost = () => {
        if (subscription?.plan_type === 'free' || !subscription) return stats.totalCost;
        const internalLimit = stats.internalApiLimitUsd || (stats.userPlanPrice ? stats.userPlanPrice * 0.65 : 12.35);
        const planPrice = stats.userPlanPrice || (subscription?.captured_cost ? parseFloat(subscription.captured_cost) : 19.00);
        if (internalLimit <= 0) return stats.totalCost;
        return Math.min((stats.totalCost / internalLimit) * planPrice, planPrice);
    };

    // Customer-facing displayed budget limit ($19.00 for Pro, $50.00 for Studio)
    const getDisplayedLimit = () => {
        if (subscription?.plan_type === 'free' || !subscription) return 0;
        return stats.userPlanPrice || (subscription?.captured_cost ? parseFloat(subscription.captured_cost) : 19.00);
    };

    // Get budget bar color based on percentage
    const getBudgetColor = (percentage) => {
        if (percentage >= 90) return '#ef4444'; // red
        if (percentage >= 70) return '#f59e0b'; // yellow
        return '#22c55e'; // green
    };

    // Get filtered usage data based on time period
    const getFilteredUsage = () => {
        const days = timePeriod === 'day' ? 1 : timePeriod === 'week' ? 7 : 30;
        return dailyUsage.slice(-days);
    };

    if (loading) {
        return (
            <div className="dashboard dashboard--loading">
                <Loader2 className="dashboard__spinner" />
                <p>Loading your dashboard...</p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <aside className="dashboard__sidebar">
                <a href="/" className="dashboard__brand">REVTRONE</a>

                <nav className="dashboard__nav">
                    <button
                        className={`dashboard__navItem ${activeTab === 'overview' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <Activity size={18} />
                        Overview
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'usage' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('usage')}
                    >
                        <BarChart3 size={18} />
                        Model Health
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'chats' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('chats')}
                    >
                        <MessageSquare size={18} />
                        Chats
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'billing' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('billing')}
                    >
                        <DollarSign size={18} />
                        Billing
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'team' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('team')}
                    >
                        <User size={18} />
                        Team
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'profile' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => {
                            setActiveTab('profile');
                            setEditingName(profile?.full_name || '');
                        }}
                    >
                        <User size={18} />
                        Profile
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'logs' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('logs')}
                    >
                        <History size={18} />
                        API Logs
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'devices' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('devices')}
                    >
                        <Monitor size={18} />
                        Devices
                    </button>
                    <button
                        className={`dashboard__navItem ${activeTab === 'account' ? 'dashboard__navItem--active' : ''}`}
                        onClick={() => setActiveTab('account')}
                    >
                        <Key size={18} />
                        Account
                    </button>
                </nav>

                <div className="dashboard__user">
                    <div className="dashboard__userAvatar">
                        <User size={20} />
                    </div>
                    <div className="dashboard__userInfo">
                        <span className="dashboard__userName">{user?.email?.split('@')[0]}</span>
                        <span className="dashboard__userEmail">{user?.email}</span>
                    </div>
                    <button className="dashboard__logout" onClick={handleSignOut} title="Sign out">
                        <LogOut size={18} />
                    </button>
                </div>
            </aside>

            <main className="dashboard__main">
                <header className="dashboard__header">
                    <a href="/" className="dashboard__back">
                        <ArrowLeft size={18} />
                        Back to Home
                    </a>
                    <h1 className="dashboard__title">
                        {activeTab === 'overview' && 'Dashboard Overview'}
                        {activeTab === 'usage' && 'Model Health History'}
                        {activeTab === 'chats' && 'Recent Chats'}
                        {activeTab === 'billing' && 'Billing History'}
                        {activeTab === 'team' && 'Team Usage'}
                        {activeTab === 'profile' && 'Profile Settings'}
                        {activeTab === 'logs' && 'API Cost & Usage Logs'}
                        {activeTab === 'devices' && 'Connected Devices & Licensing'}
                        {activeTab === 'account' && 'Account Settings'}
                    </h1>
                </header>

                <div className="dashboard__content">
                    {activeTab === 'overview' && (
                        <>
                            {/* Usage Threshold Alert Banners (50%, 80%, 100%) */}
                            {getBudgetPercentage() >= 100 && (
                                <div className="dashboard__alert dashboard__alert--critical">
                                    <div className="dashboard__alertHeader">🛑 100% Monthly Plan Limit Reached</div>
                                    <div className="dashboard__alertDesc">
                                        You have reached 100% of your monthly plan budget (${getDisplayedCost().toFixed(2)} / ${getDisplayedLimit().toFixed(2)}). 
                                        Proxy requests are paused. Switch to <strong>Bring Your Own API Key (BYOK)</strong> mode in settings to enjoy unlimited access, or upgrade your plan.
                                    </div>
                                </div>
                            )}

                            {getBudgetPercentage() >= 80 && getBudgetPercentage() < 100 && (
                                <div className="dashboard__alert dashboard__alert--warning">
                                    <div className="dashboard__alertHeader">⚠️ 80% Usage Warning</div>
                                    <div className="dashboard__alertDesc">
                                        You have used {getBudgetPercentage().toFixed(1)}% of your monthly plan usage (${getDisplayedCost().toFixed(2)} / ${getDisplayedLimit().toFixed(2)}). 
                                        Upgrade your plan or configure your own API key to ensure uninterrupted service.
                                    </div>
                                </div>
                            )}

                            {getBudgetPercentage() >= 50 && getBudgetPercentage() < 80 && (
                                <div className="dashboard__alert dashboard__alert--info">
                                    <div className="dashboard__alertHeader">ℹ️ 50% Usage Notice</div>
                                    <div className="dashboard__alertDesc">
                                        You have used {getBudgetPercentage().toFixed(1)}% of your monthly plan usage (${getDisplayedCost().toFixed(2)} / ${getDisplayedLimit().toFixed(2)}).
                                    </div>
                                </div>
                            )}

                            {/* Budget Progress Bar */}
                            <div className="dashboard__budgetSection">
                                <div className="dashboard__budgetHeader">
                                    <h3>Monthly Plan Usage</h3>
                                    <span className="dashboard__budgetAmount">
                                        {subscription?.plan_type === 'free' || !subscription
                                            ? `BYOK Mode (Direct Key Access)`
                                            : `$${getDisplayedCost().toFixed(2)} / $${getDisplayedLimit().toFixed(2)}`}
                                    </span>
                                </div>
                                <div className="dashboard__progressBar">
                                    <div
                                        className="dashboard__progressFill"
                                        style={{
                                            width: `${getBudgetPercentage()}%`,
                                            backgroundColor: getBudgetColor(getBudgetPercentage())
                                        }}
                                    />
                                </div>
                                <div className="dashboard__budgetMeta">
                                    <span>
                                        {subscription?.plan_type === 'free' || !subscription
                                            ? 'Full Unrestricted Direct Access'
                                            : `${getBudgetPercentage().toFixed(1)}% of plan credit used`}
                                    </span>
                                    <span>Est. monthly usage: ${estimateMonthlyCost().toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Stats & Plan Allowance Cards */}
                            <div className="dashboard__cards">
                                <div className="dashboard__card">
                                    <span className="dashboard__cardLabel">Current Plan</span>
                                    <span className="dashboard__cardValue">{subscription?.plan_type ? subscription.plan_type.toUpperCase() : 'FREE'}</span>
                                    <a href="/#pricing" className="dashboard__cardLink">Upgrade Plan →</a>
                                </div>
                                <div className="dashboard__card">
                                    <span className="dashboard__cardLabel">Plan API Budget</span>
                                    <span className="dashboard__cardValue">
                                        {subscription?.plan_type === 'free' || !subscription ? 'BYOK (Own Key)' : `$${getDisplayedLimit().toFixed(2)}`}
                                    </span>
                                    <span className="dashboard__cardSub">
                                        {subscription?.plan_type === 'free' || !subscription ? 'Direct OpenRouter/API' : 'Monthly Budget Allowance'}
                                    </span>
                                </div>
                                <div className="dashboard__card">
                                    <span className="dashboard__cardLabel">Budget Used</span>
                                    <span className="dashboard__cardValue">
                                        {subscription?.plan_type === 'free' || !subscription ? `$${stats.totalCost.toFixed(4)}` : `$${getDisplayedCost().toFixed(2)}`}
                                    </span>
                                    <span className="dashboard__cardSub">
                                        {subscription?.plan_type === 'free' || !subscription ? 'Direct Unrestricted Usage' : `${getBudgetPercentage().toFixed(1)}% of plan budget`}
                                    </span>
                                </div>
                                <div className="dashboard__card">
                                    <span className="dashboard__cardLabel">API Mode</span>
                                    <span className="dashboard__cardValue">
                                        {subscription?.plan_type === 'free' || !subscription ? 'BYOK (Own Key)' : 'Managed Proxy'}
                                    </span>
                                    <span className="dashboard__cardSub">
                                        {subscription?.plan_type === 'free' || !subscription ? 'Full Unrestricted Access' : 'Included in Plan'}
                                    </span>
                                </div>
                            </div>

                            {/* Model Breakdown */}
                            {modelBreakdown.length > 0 && (
                                <div className="dashboard__section">
                                    <h2 className="dashboard__sectionTitle">Cost by Model</h2>
                                    <div className="dashboard__modelTable">
                                        <div className="dashboard__modelHeader">
                                            <span>Model</span>
                                            <span>Requests</span>
                                            <span>Tokens</span>
                                            <span>Cost</span>
                                        </div>
                                        {modelBreakdown.map((model, i) => (
                                            <div key={i} className="dashboard__modelRow">
                                                <span className="dashboard__modelName">{model.model}</span>
                                                <span>{model.count}</span>
                                                <span>{model.tokens.toLocaleString()}</span>
                                                <span>${model.cost.toFixed(4)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Usage Chart with Time Toggle */}
                            <div className="dashboard__section">
                                <div className="dashboard__chartHeader">
                                    <h2 className="dashboard__sectionTitle">Usage Over Time</h2>
                                    <div className="dashboard__timeTabs">
                                        <button
                                            className={timePeriod === 'day' ? 'active' : ''}
                                            onClick={() => setTimePeriod('day')}
                                        >Day</button>
                                        <button
                                            className={timePeriod === 'week' ? 'active' : ''}
                                            onClick={() => setTimePeriod('week')}
                                        >Week</button>
                                        <button
                                            className={timePeriod === 'month' ? 'active' : ''}
                                            onClick={() => setTimePeriod('month')}
                                        >Month</button>
                                    </div>
                                </div>
                                <ResponsiveContainer width="100%" height={200}>
                                    <AreaChart data={getFilteredUsage()}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip />
                                        <Area type="monotone" dataKey="tokens" stroke="#171717" fill="#e5e5e5" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Cost Calculator */}
                            <div className="dashboard__section">
                                <h2 className="dashboard__sectionTitle">Cost Estimator</h2>
                                <div className="dashboard__calculator">
                                    <div className="dashboard__calcRow">
                                        <span>Average daily cost:</span>
                                        <strong>${(estimateMonthlyCost() / 30).toFixed(4)}</strong>
                                    </div>
                                    <div className="dashboard__calcRow">
                                        <span>Projected weekly cost:</span>
                                        <strong>${(estimateMonthlyCost() / 4.3).toFixed(2)}</strong>
                                    </div>
                                    <div className="dashboard__calcRow">
                                        <span>Projected monthly cost:</span>
                                        <strong>${estimateMonthlyCost().toFixed(2)}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Export & Budget Settings */}
                            <div className="dashboard__section">
                                <h2 className="dashboard__sectionTitle">Settings & Export</h2>
                                <div className="dashboard__settingsRow">
                                    <div className="dashboard__budgetInput">
                                        <label>Monthly Budget Limit ($)</label>
                                        <input
                                            type="number"
                                            value={budgetLimit || ''}
                                            onChange={(e) => setBudgetLimit(parseFloat(e.target.value) || null)}
                                            placeholder="5.00"
                                        />
                                        <button
                                            onClick={() => handleSaveBudgetLimit(budgetLimit)}
                                            disabled={savingBudget}
                                        >
                                            {savingBudget ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                    <button className="dashboard__exportBtn" onClick={exportUsageCSV}>
                                        Export Usage Data (CSV)
                                    </button>
                                </div>
                            </div>

                            {/* Account Info */}
                            <div className="dashboard__section">
                                <h2 className="dashboard__sectionTitle">Account Info</h2>
                                <div className="dashboard__infoRow">
                                    <span className="dashboard__infoLabel">Email:</span>
                                    <span className="dashboard__infoValue">{user?.email}</span>
                                </div>
                                <div className="dashboard__infoRow">
                                    <span className="dashboard__infoLabel">Member Since:</span>
                                    <span className="dashboard__infoValue">{formatDate(user?.created_at)}</span>
                                </div>
                            </div>

                            <div className="dashboard__section">
                                <h2 className="dashboard__sectionTitle">
                                    Token Usage
                                    <span className="dashboard__sectionBadge">Last 7 days</span>
                                </h2>
                                <div className="dashboard__chart">
                                    <ResponsiveContainer width="100%" height={280}>
                                        <AreaChart data={dailyUsage} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#171717" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#171717" stopOpacity={0.02} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 12, fill: '#737373' }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 12, fill: '#737373' }}
                                                axisLine={false}
                                                tickLine={false}
                                                width={40}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #e5e5e5',
                                                    borderRadius: 8,
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                    padding: '12px 16px',
                                                }}
                                                labelStyle={{ color: '#171717', fontWeight: 600, marginBottom: 4 }}
                                                itemStyle={{ color: '#737373' }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="tokens"
                                                stroke="#171717"
                                                strokeWidth={2}
                                                fillOpacity={1}
                                                fill="url(#colorTokens)"
                                                name="Tokens"
                                                dot={{ fill: '#171717', strokeWidth: 0, r: 4 }}
                                                activeDot={{ fill: '#171717', strokeWidth: 0, r: 6 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'usage' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Model Health History</h2>
                            {recentSnapshots.length > 0 ? (
                                <table className="dashboard__table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Health Score</th>
                                            <th>Clashes</th>
                                            <th>Warnings</th>
                                            <th>Elements</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentSnapshots.map((snapshot) => (
                                            <tr key={snapshot.id}>
                                                <td>{formatDate(snapshot.date)}</td>
                                                <td>
                                                    <span className={`dashboard__score ${snapshot.health_score >= 80 ? 'dashboard__score--good' : snapshot.health_score >= 50 ? 'dashboard__score--warning' : 'dashboard__score--bad'}`}>
                                                        {snapshot.health_score}%
                                                    </span>
                                                </td>
                                                <td>{snapshot.clashes}</td>
                                                <td>{snapshot.warnings}</td>
                                                <td>{snapshot.total_elements?.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="dashboard__empty">No health snapshots recorded yet.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'chats' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Recent Conversations</h2>
                            {recentChats.length > 0 ? (
                                <div className="dashboard__chatList">
                                    {recentChats.map((chat) => (
                                        <div key={chat.id} className="dashboard__chatItem">
                                            <MessageSquare size={18} className="dashboard__chatIcon" />
                                            <div className="dashboard__chatInfo">
                                                <span className="dashboard__chatName">{chat.name}</span>
                                                <span className="dashboard__chatDate">{formatDate(chat.last_active)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="dashboard__empty">No conversations yet. Start chatting in the Revitrone desktop app for Revit or Rhino!</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'logs' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">API Transaction History</h2>
                            {apiLogs.length > 0 ? (
                                <div className="dashboard__tableWrapper">
                                    <table className="dashboard__table">
                                        <thead>
                                            <tr>
                                                <th>Date & Time</th>
                                                <th>Endpoint</th>
                                                <th>Tokens</th>
                                                <th>Cost</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {apiLogs.map((log) => (
                                                <tr key={log.id}>
                                                    <td className="dashboard__td--time">
                                                        {new Date(log.created_at).toLocaleString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td>
                                                        <code className="dashboard__endpoint">{log.endpoint || 'ai_generate'}</code>
                                                    </td>
                                                    <td>{log.tokens_used?.toLocaleString() || 0}</td>
                                                    <td>
                                                        <span className="dashboard__cost">
                                                            ${(log.cost_usd || 0).toFixed(4)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`dashboard__statusBadge ${log.response_status < 300 ? 'dashboard__statusBadge--success' : 'dashboard__statusBadge--error'}`}>
                                                            {log.response_status || 200}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dashboard__empty">
                                    <Activity size={48} style={{ opacity: 0.2, marginBottom: 16 }} />
                                    <p>No API activity recorded yet. Call the Revitrone API to see logs.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'profile' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Edit Profile</h2>

                            {profileMessage.text && (
                                <div className={`dashboard__message dashboard__message--${profileMessage.type}`}>
                                    {profileMessage.text}
                                </div>
                            )}

                            <div className="dashboard__form">
                                <div className="dashboard__formGroup">
                                    <label className="dashboard__label" htmlFor="fullName">Display Name</label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        className="dashboard__input"
                                        value={editingName}
                                        onChange={(e) => setEditingName(e.target.value)}
                                        placeholder="Enter your display name"
                                    />
                                </div>

                                <div className="dashboard__formGroup">
                                    <label className="dashboard__label">Email</label>
                                    <p className="dashboard__formValue">{user?.email}</p>
                                </div>

                                <div className="dashboard__formGroup">
                                    <label className="dashboard__label">Member Since</label>
                                    <p className="dashboard__formValue">{formatDate(user?.created_at)}</p>
                                </div>

                                <button
                                    className="dashboard__saveBtn"
                                    onClick={handleSaveProfile}
                                    disabled={savingProfile}
                                >
                                    {savingProfile ? (
                                        <Loader2 size={16} className="dashboard__spinner--inline" />
                                    ) : (
                                        <Save size={16} />
                                    )}
                                    {savingProfile ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'billing' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Payment History</h2>
                            {billingHistory.length === 0 ? (
                                <p className="dashboard__emptyText">No billing history yet.</p>
                            ) : (
                                <div className="dashboard__billingTable">
                                    <div className="dashboard__billingHeader">
                                        <span>Date</span>
                                        <span>Plan</span>
                                        <span>Status</span>
                                        <span>Amount</span>
                                    </div>
                                    {billingHistory.map((item, i) => (
                                        <div key={i} className="dashboard__billingRow">
                                            <span>{formatDate(item.created_at)}</span>
                                            <span>{item.plan_type || 'Free'}</span>
                                            <span className={`dashboard__status dashboard__status--${item.status}`}>
                                                {item.status}
                                            </span>
                                            <span>${parseFloat(item.price_usd || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <h2 className="dashboard__sectionTitle" style={{ marginTop: '32px' }}>Plan Comparison</h2>
                            <div className="dashboard__planGrid">
                                <div className="dashboard__planCard">
                                    <h3>Free</h3>
                                    <p className="dashboard__planPrice">$0<span>/mo</span></p>
                                    <ul>
                                        <li>Use your own API key</li>
                                        <li>1 device login</li>
                                        <li>No image generation</li>
                                        <li>Basic chat only</li>
                                    </ul>
                                    <button disabled={subscription?.plan_type === 'Free'}>
                                        {subscription?.plan_type === 'Free' ? 'Current Plan' : 'Downgrade'}
                                    </button>
                                </div>
                                <div className="dashboard__planCard dashboard__planCard--popular">
                                    <span className="dashboard__planBadge">Popular</span>
                                    <h3>Pro</h3>
                                    <p className="dashboard__planPrice">$19<span>/mo</span></p>
                                    <ul>
                                        <li>50,000 tokens/month</li>
                                        <li>2 device logins</li>
                                        <li>Image generation included</li>
                                        <li>All AI models included</li>
                                        <li>Priority email support</li>
                                    </ul>
                                    <button disabled={subscription?.plan_type === 'Pro'}>
                                        {subscription?.plan_type === 'Pro' ? 'Current Plan' : 'Upgrade'}
                                    </button>
                                </div>
                                <div className="dashboard__planCard">
                                    <h3>Studio</h3>
                                    <p className="dashboard__planPrice">$50<span>/mo</span></p>
                                    <ul>
                                        <li>150,000 tokens/month</li>
                                        <li>5 device logins</li>
                                        <li>Image generation & Collab</li>
                                        <li>Priority support</li>
                                    </ul>
                                    <button disabled={subscription?.plan_type === 'Studio'}>
                                        {subscription?.plan_type === 'Studio' ? 'Current Plan' : 'Upgrade'}
                                    </button>
                                </div>
                                <div className="dashboard__planCard">
                                    <h3>Enterprise</h3>
                                    <p className="dashboard__planPrice">Custom</p>
                                    <ul>
                                        <li>Unlimited tokens</li>
                                        <li>10+ device logins</li>
                                        <li>Team dashboard</li>
                                        <li>Dedicated support</li>
                                    </ul>
                                    <button disabled={subscription?.plan_type === 'Enterprise'}>
                                        {subscription?.plan_type === 'Enterprise' ? 'Current Plan' : 'Contact Sales'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'team' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Team Members</h2>
                            {teamMembers.length === 0 ? (
                                <div className="dashboard__emptyState">
                                    <p>You're not part of a team yet.</p>
                                    <p className="dashboard__emptyHint">Team features are available on Enterprise plans.</p>
                                </div>
                            ) : (
                                <div className="dashboard__teamTable">
                                    <div className="dashboard__teamHeader">
                                        <span>Member</span>
                                        <span>Email</span>
                                        <span>Tokens Used</span>
                                    </div>
                                    {teamMembers.map((member, i) => (
                                        <div key={i} className="dashboard__teamRow">
                                            <span>{member.full_name || 'Unknown'}</span>
                                            <span>{member.email}</span>
                                            <span>{(member.tokens_used || 0).toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'devices' && (
                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Registered Devices</h2>
                            <p className="dashboard__hint">
                                Your current plan allows up to <strong>{subscription?.max_machines || 1}</strong> active devices.
                                You have <strong>{remainingDeactivations}</strong> deactivations remaining this month.
                            </p>

                            {deviceMessage.text && (
                                <div className={`dashboard__message dashboard__message--${deviceMessage.type}`}>
                                    {deviceMessage.text}
                                </div>
                            )}

                            {machines.length > 0 ? (
                                <div className="dashboard__deviceList">
                                    {machines.map((device) => (
                                        <div key={device.id} className="dashboard__deviceCard">
                                            <div className="dashboard__deviceIcon">
                                                {device.os?.toLowerCase() === 'mac' ? <Smartphone size={24} /> : <Monitor size={24} />}
                                            </div>
                                            <div className="dashboard__deviceInfo">
                                                <h4 className="dashboard__deviceName">{device.machine_name || 'Unknown Device'}</h4>
                                                <div className="dashboard__deviceMeta">
                                                    <span>ID: {device.machine_id.substring(0, 12)}...</span>
                                                    <span>•</span>
                                                    <span>Last used: {formatDate(device.last_used_at)}</span>
                                                </div>
                                            </div>
                                            <button
                                                className="dashboard__deviceRemove"
                                                onClick={() => handleRemoveMachine(device.machine_id)}
                                                disabled={deactivatingId === device.machine_id}
                                            >
                                                {deactivatingId === device.machine_id ? (
                                                    <Loader2 size={16} className="dashboard__spinner--inline" />
                                                ) : (
                                                    <Trash2 size={16} />
                                                )}
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="dashboard__empty">
                                    <Monitor size={48} style={{ opacity: 0.2, marginBottom: 16 }} />
                                    <p>No devices registered yet. Log in to the Revitrone app on your PC to register it.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'account' && (

                        <div className="dashboard__section">
                            <h2 className="dashboard__sectionTitle">Your User ID</h2>
                            <p className="dashboard__hint">
                                Use this ID for API integrations and support requests.
                            </p>
                            <div className="dashboard__apiKey">
                                <code className="dashboard__apiKeyValue">
                                    {user?.id}
                                </code>
                                <button className="dashboard__apiKeyBtn" onClick={copyUserId}>
                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            </div>

                            <h2 className="dashboard__sectionTitle" style={{ marginTop: '32px' }}>Token Usage</h2>
                            <div className="dashboard__progress">
                                <div className="dashboard__progressBar">
                                    <div
                                        className="dashboard__progressFill"
                                        style={{ width: `${Math.min(100, (stats.tokensUsed / (stats.tokenBalance + stats.tokensUsed)) * 100 || 0)}%` }}
                                    />
                                </div>
                                <span className="dashboard__progressText">
                                    {stats.tokensUsed.toLocaleString()} tokens used • {stats.tokenBalance.toLocaleString()} remaining
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
